import '../styles/FloorLayout.css';
import '../styles/ViewAthletes.css';

import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

import {
  collection,
  deleteField,
  getDoc,
  getDocs,
  getFirestore,
  onSnapshot,
  query,
  setDoc,
  updateDoc,
} from 'firebase/firestore';
import {
  useNavigate,
  useParams,
} from 'react-router-dom';

import firebase from '../firebase';
import { sortAthletesByNumber } from '../helper';
import { Athlete } from '../types/types';
import {
  floorLayoutDocumentRef,
  placementsFromFirestoreData,
} from '../utils/floorLayoutFirestore';
import {
  Button,
  Card,
  Container,
  Typography,
} from './common';

const MAT_COUNT = 9;
const MAT_DIVIDER_INDICES = [1, 2, 3, 4, 5, 6, 7, 8] as const;

const FLOOR_INNER_WIDTH = 1600;
const FLOOR_INNER_HEIGHT = 900;

const DRAG_THRESHOLD_PX = 10;
const MIN_SCALE = 0.35;
const MAX_SCALE = 3.5;

/** Roster (palette) touch: hold this long to grab; shorter contact = scroll / tap only. */
const ROSTER_LONG_PRESS_MS = 200;
/** If the finger moves this far before long-press completes, treat as scroll and cancel grab. */
const ROSTER_LONG_PRESS_MOVE_CANCEL_PX = 10;

/** One-finger floor pan (touch only). Pointer/mouse pan stays 1:1 in `movePointerDrag`. */
const TOUCH_PAN_DAMPING = 0.8;

function clamp(n: number, lo: number, hi: number): number {
  return Math.min(hi, Math.max(lo, n));
}

function distance(a: Touch, b: Touch): number {
  const dx = a.clientX - b.clientX;
  const dy = a.clientY - b.clientY;
  return Math.hypot(dx, dy);
}

function midpoint(a: Touch, b: Touch): { x: number; y: number } {
  return { x: (a.clientX + b.clientX) / 2, y: (a.clientY + b.clientY) / 2 };
}

/** Normalized key for storage / grouping (matches AthleteCard teamplacement values, lowercased). */
function teamKeyForAthlete(a: Athlete): string {
  const t = (a.teamplacement ?? '').trim().toLowerCase();
  return t || '__unassigned__';
}

const TEAMPLACEMENT_LABELS: Record<string, string> = {
  coed: 'COED',
  allgirl: 'ALLGIRL',
  level4: 'LVL4',
  yes: 'Yes?',
  maybe: 'Maybe',
  no: 'No',
};

function teamLabel(teamKey: string): string {
  if (teamKey === '__unassigned__') return 'Unassigned';
  return TEAMPLACEMENT_LABELS[teamKey] ?? teamKey;
}

function deriveTeamKeys(athletes: Athlete[]): string[] {
  const keys = new Set<string>();
  athletes.forEach((a) => keys.add(teamKeyForAthlete(a)));
  const list = Array.from(keys);
  list.sort((a, b) => {
    if (a === '__unassigned__') return 1;
    if (b === '__unassigned__') return -1;
    return a.localeCompare(b);
  });
  return list;
}

interface MiniAthleteCardProps {
  athlete: Athlete;
  selected: boolean;
  onSelect: () => void;
  /** Tokens on the spring floor (vs palette strip). */
  onFloor?: boolean;
}

function MiniAthleteCard({ athlete, selected, onSelect, onFloor = false }: MiniAthleteCardProps) {
  const img = athlete.imageThumbnailUrl || athlete.imageUrl;
  return (
    <div
      role="button"
      tabIndex={0}
      className={`floor-layout-mini-card${onFloor ? ' floor-layout-mini-card--onfloor' : ''}${selected ? ' floor-layout-mini-card-selected' : ''}`}
      onClick={(e) => {
        e.stopPropagation();
        onSelect();
      }}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          onSelect();
        }
      }}
    >
      <div className="floor-layout-mini-photo">
        {img ? (
          <img src={img} alt="" draggable={false} />
        ) : (
          <span>{athlete.name.charAt(0).toUpperCase()}</span>
        )}
      </div>
      <div className="floor-layout-mini-text">
        <div className="floor-layout-mini-number-row">
          <span className="floor-layout-mini-number">#{athlete.number}</span>
          {(athlete.position ?? '').trim() ? (
            <span className="floor-layout-mini-position-pill">{(athlete.position ?? '').trim()}</span>
          ) : null}
        </div>
        <div className="floor-layout-mini-name">{athlete.name}</div>
      </div>
    </div>
  );
}

type ViewTransform = { scale: number; panX: number; panY: number };

function fitFloorInViewport(viewW: number, viewH: number): ViewTransform {
  const scale = Math.min(viewW / FLOOR_INNER_WIDTH, viewH / FLOOR_INNER_HEIGHT);
  const panX = (viewW - FLOOR_INNER_WIDTH * scale) / 2;
  const panY = (viewH - FLOOR_INNER_HEIGHT * scale) / 2;
  return { scale, panX, panY };
}

export default function FloorLayoutPage() {
  const { sessionId } = useParams<{ sessionId: string }>();
  const navigate = useNavigate();
  const [athletes, setAthletes] = useState<Athlete[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [selectedTeamKey, setSelectedTeamKey] = useState<string | null>(null);
  const [positionFilter, setPositionFilter] = useState<string>('all');
  const [selectedAthleteId, setSelectedAthleteId] = useState<string | null>(null);
  const [dragOverPalette, setDragOverPalette] = useState(false);
  /** Raw placements from sessions/{sessionId}/floorLayout/{teamKey}; shared for the session (not per user). */
  const [floorDocPlacements, setFloorDocPlacements] = useState<
    Record<string, { xPct: number; yPct: number }>
  >({});
  const [viewTransform, setViewTransform] = useState<ViewTransform>({
    scale: 1,
    panX: 0,
    panY: 0,
  });

  const viewportRef = useRef<HTMLDivElement>(null);
  const paletteRef = useRef<HTMLDivElement>(null);
  const rosterLongPressTimerRef = useRef<number | null>(null);
  const viewTransformRef = useRef(viewTransform);
  viewTransformRef.current = viewTransform;

  /** Pointer drag: palette | floor token */
  const pointerDragRef = useRef<{
    athleteId: string;
    pointerId: number;
    startClientX: number;
    startClientY: number;
    lastClientX: number;
    lastClientY: number;
    moved: boolean;
    captureEl: HTMLElement | null;
    /** Roster touch only: waiting for long-press before capture (otherwise list scrolls). */
    awaitingLongPress: boolean;
    /** Drag began on roster strip (not a floor token). Used to lock list scroll during drag. */
    fromPalette: boolean;
  } | null>(null);

  const [pointerGhost, setPointerGhost] = useState<{
    athleteId: string;
    clientX: number;
    clientY: number;
  } | null>(null);

  /** While true, roster scroll containers use touch-action:none + touchmove preventDefault (dnd-kit-style). */
  const [paletteRosterDragLock, setPaletteRosterDragLock] = useState(false);
  const paletteLockSetForThisDragRef = useRef(false);

  const suppressSelectClickRef = useRef(false);
  const suppressViewportClearRef = useRef(false);
  /** True after a 2-finger gesture on the floor viewport; eats the synthetic click so pinch does not toggle selection. */
  const viewportSawMultitouchRef = useRef(false);
  const suppressAthleteSelectAfterPinchRef = useRef(false);
  const pinchSelectSuppressTimerRef = useRef<number | null>(null);
  /**
   * Second finger on the floor while the first is an active pointer-drag on a token.
   * endPointerDrag must suppress the synthetic click because !moved skips that today.
   */
  const multitouchDuringFloorPointerDragRef = useRef(false);

  const pinchRef = useRef<{
    startDist: number;
    startScale: number;
  } | null>(null);

  const panRef = useRef<{
    lastX: number;
    lastY: number;
  } | null>(null);

  const floorPointerPanRef = useRef<{
    pointerId: number;
    lastX: number;
    lastY: number;
    moved: boolean;
  } | null>(null);

  const db = getFirestore(firebase);

  useEffect(() => {
    document.body.classList.add('floor-layout-body-lock');
    return () => {
      document.body.classList.remove('floor-layout-body-lock');
      if (rosterLongPressTimerRef.current !== null) {
        clearTimeout(rosterLongPressTimerRef.current);
        rosterLongPressTimerRef.current = null;
      }
      if (pinchSelectSuppressTimerRef.current !== null) {
        clearTimeout(pinchSelectSuppressTimerRef.current);
        pinchSelectSuppressTimerRef.current = null;
      }
    };
  }, []);

  const consumeAthleteSelectSuppressedByPinch = useCallback((): boolean => {
    if (!suppressAthleteSelectAfterPinchRef.current) return false;
    suppressAthleteSelectAfterPinchRef.current = false;
    if (pinchSelectSuppressTimerRef.current !== null) {
      clearTimeout(pinchSelectSuppressTimerRef.current);
      pinchSelectSuppressTimerRef.current = null;
    }
    return true;
  }, []);

  /** After multitouch on the floor, block the synthetic click in capture phase (before React sees it). */
  const armSuppressSyntheticClickAfterViewportMultitouch = useCallback(() => {
    if (!viewportSawMultitouchRef.current) return;
    viewportSawMultitouchRef.current = false;
    suppressAthleteSelectAfterPinchRef.current = true;
    if (pinchSelectSuppressTimerRef.current !== null) {
      clearTimeout(pinchSelectSuppressTimerRef.current);
    }
    pinchSelectSuppressTimerRef.current = window.setTimeout(() => {
      suppressAthleteSelectAfterPinchRef.current = false;
      pinchSelectSuppressTimerRef.current = null;
    }, 600);
  }, []);

  const onFloorViewportClickCapture = useCallback((ev: MouseEvent) => {
    if (!suppressAthleteSelectAfterPinchRef.current) return;
    suppressAthleteSelectAfterPinchRef.current = false;
    if (pinchSelectSuppressTimerRef.current !== null) {
      clearTimeout(pinchSelectSuppressTimerRef.current);
      pinchSelectSuppressTimerRef.current = null;
    }
    ev.preventDefault();
    ev.stopPropagation();
    ev.stopImmediatePropagation();
  }, []);

  /** Pinch starts: drop pointer-drag + arm click suppression (first finger was often a captured pointer on a token). */
  const cancelPointerDragForViewportPinch = useCallback(() => {
    const d = pointerDragRef.current;
    if (rosterLongPressTimerRef.current !== null) {
      clearTimeout(rosterLongPressTimerRef.current);
      rosterLongPressTimerRef.current = null;
    }
    if (d) {
      try {
        d.captureEl?.releasePointerCapture(d.pointerId);
      } catch {
        /* ignore */
      }
      pointerDragRef.current = null;
      setPointerGhost(null);
      setDragOverPalette(false);
      paletteLockSetForThisDragRef.current = false;
      setPaletteRosterDragLock(false);
    }
    suppressSelectClickRef.current = true;
    suppressAthleteSelectAfterPinchRef.current = true;
    if (pinchSelectSuppressTimerRef.current !== null) {
      clearTimeout(pinchSelectSuppressTimerRef.current);
    }
    pinchSelectSuppressTimerRef.current = window.setTimeout(() => {
      suppressAthleteSelectAfterPinchRef.current = false;
      pinchSelectSuppressTimerRef.current = null;
    }, 600);
  }, []);

  /** Block browser scroll on the roster while dragging to the floor (pointer capture alone is not enough on iOS). */
  useEffect(() => {
    const root = paletteRef.current;
    if (!root || !paletteRosterDragLock) return;
    const blockNativeScroll = (te: TouchEvent) => {
      te.preventDefault();
    };
    root.addEventListener('touchmove', blockNativeScroll, {
      passive: false,
      capture: true,
    });
    return () => {
      root.removeEventListener('touchmove', blockNativeScroll, { capture: true });
    };
  }, [paletteRosterDragLock]);

  useEffect(() => {
    async function fetchAthletes() {
      if (!sessionId) {
        setError('Session ID is missing');
        setLoading(false);
        return;
      }
      try {
        setLoading(true);
        setError('');
        const athletesRef = collection(db, `sessions/${sessionId}/athletes`);
        const q = query(athletesRef);
        const snap = await getDocs(q);
        const list: Athlete[] = [];
        snap.forEach((d) => {
          list.push({ id: d.id, ...d.data() } as Athlete);
        });
        setAthletes(sortAthletesByNumber(list));
      } catch (err) {
        console.error(err);
        setError('Failed to load athletes');
      } finally {
        setLoading(false);
      }
    }
    fetchAthletes();
  }, [sessionId, db]);

  const teamKeys = useMemo(() => deriveTeamKeys(athletes), [athletes]);

  useEffect(() => {
    if (teamKeys.length && selectedTeamKey === null) {
      setSelectedTeamKey(teamKeys[0]);
    }
  }, [teamKeys, selectedTeamKey]);

  useEffect(() => {
    setSelectedAthleteId(null);
    setPositionFilter('all');
  }, [selectedTeamKey]);

  useEffect(() => {
    if (!sessionId || !selectedTeamKey) {
      setFloorDocPlacements({});
      return;
    }
    const ref = floorLayoutDocumentRef(db, sessionId, selectedTeamKey);
    const unsub = onSnapshot(
      ref,
      (snap) => {
        if (!snap.exists()) {
          setFloorDocPlacements({});
          return;
        }
        const data = snap.data();
        setFloorDocPlacements(placementsFromFirestoreData(data?.placements));
      },
      (err) => {
        console.error(err);
        setFloorDocPlacements({});
      },
    );
    return () => unsub();
  }, [sessionId, selectedTeamKey, db]);

  const roster = useMemo(() => {
    if (!selectedTeamKey) return [];
    return athletes.filter((a) => teamKeyForAthlete(a) === selectedTeamKey);
  }, [athletes, selectedTeamKey]);

  const teamById = useMemo(() => {
    const m = new Map<string, Athlete>();
    roster.forEach((a) => m.set(a.id, a));
    return m;
  }, [roster]);

  const placements = useMemo(() => {
    const rosterIds = new Set(roster.map((a) => a.id));
    const m: Record<string, { xPct: number; yPct: number }> = {};
    for (const [id, pos] of Object.entries(floorDocPlacements)) {
      if (rosterIds.has(id)) m[id] = pos;
    }
    return m;
  }, [floorDocPlacements, roster]);

  const positionFilteredRoster = useMemo(() => {
    if (positionFilter === 'all') return roster;
    return roster.filter((a) => a.position === positionFilter);
  }, [roster, positionFilter]);

  const paletteAthletes = useMemo(
    () => positionFilteredRoster.filter((a) => !placements[a.id]),
    [positionFilteredRoster, placements],
  );

  const clientToFloorPercent = useCallback((clientX: number, clientY: number) => {
    const vp = viewportRef.current;
    if (!vp) return null;
    const vr = vp.getBoundingClientRect();
    const { scale, panX, panY } = viewTransformRef.current;
    const vx = clientX - vr.left;
    const vy = clientY - vr.top;
    const xInner = (vx - panX) / scale;
    const yInner = (vy - panY) / scale;
    const xPct = (xInner / FLOOR_INNER_WIDTH) * 100;
    const yPct = (yInner / FLOOR_INNER_HEIGHT) * 100;
    if (xPct < 0 || xPct > 100 || yPct < 0 || yPct > 100) return null;
    return { xPct, yPct };
  }, []);

  const placeAthlete = useCallback(
    async (athleteId: string, xPct: number, yPct: number) => {
      if (!sessionId || !selectedTeamKey) return;
      const pos = {
        xPct: clamp(xPct, 0, 100),
        yPct: clamp(yPct, 0, 100),
      };
      const ref = floorLayoutDocumentRef(db, sessionId, selectedTeamKey);
      try {
        const snap = await getDoc(ref);
        if (!snap.exists()) {
          await setDoc(ref, { placements: {} });
        }
        await updateDoc(ref, { [`placements.${athleteId}`]: pos });
      } catch (err) {
        console.error(err);
      }
    },
    [sessionId, selectedTeamKey, db],
  );

  const removeAthlete = useCallback(
    async (athleteId: string) => {
      if (!sessionId || !selectedTeamKey) return;
      const ref = floorLayoutDocumentRef(db, sessionId, selectedTeamKey);
      try {
        const snap = await getDoc(ref);
        if (!snap.exists()) return;
        await updateDoc(ref, { [`placements.${athleteId}`]: deleteField() });
      } catch (err) {
        console.error(err);
      }
    },
    [sessionId, selectedTeamKey, db],
  );

  const resetFloorView = useCallback(() => {
    const vp = viewportRef.current;
    if (!vp) return;
    const cr = vp.getBoundingClientRect();
    setViewTransform(fitFloorInViewport(cr.width, cr.height));
  }, []);

  useEffect(() => {
    const el = viewportRef.current;
    if (!el || loading) return;
    const ro = new ResizeObserver(() => {
      const cr = el.getBoundingClientRect();
      setViewTransform(fitFloorInViewport(cr.width, cr.height));
    });
    ro.observe(el);
    const cr = el.getBoundingClientRect();
    setViewTransform(fitFloorInViewport(cr.width, cr.height));
    return () => ro.disconnect();
  }, [loading, sessionId, selectedTeamKey]);

  const endPointerDrag = useCallback(
    (e: PointerEvent) => {
      const drag = pointerDragRef.current;
      if (!drag || e.pointerId !== drag.pointerId) return;
      if (rosterLongPressTimerRef.current !== null) {
        clearTimeout(rosterLongPressTimerRef.current);
        rosterLongPressTimerRef.current = null;
      }
      pointerDragRef.current = null;
      setPointerGhost(null);
      setDragOverPalette(false);
      paletteLockSetForThisDragRef.current = false;
      setPaletteRosterDragLock(false);
      try {
        drag.captureEl?.releasePointerCapture(e.pointerId);
      } catch {
        /* ignore */
      }

      if (!drag.moved) {
        if (multitouchDuringFloorPointerDragRef.current) {
          suppressSelectClickRef.current = true;
          multitouchDuringFloorPointerDragRef.current = false;
        }
        return;
      }
      suppressSelectClickRef.current = true;

      const paletteEl = paletteRef.current;
      const vp = viewportRef.current;
      if (paletteEl) {
        const pr = paletteEl.getBoundingClientRect();
        if (
          e.clientX >= pr.left &&
          e.clientX <= pr.right &&
          e.clientY >= pr.top &&
          e.clientY <= pr.bottom
        ) {
          removeAthlete(drag.athleteId);
          return;
        }
      }

      if (vp) {
        const vr = vp.getBoundingClientRect();
        if (
          e.clientX >= vr.left &&
          e.clientX <= vr.right &&
          e.clientY >= vr.top &&
          e.clientY <= vr.bottom
        ) {
          const pct = clientToFloorPercent(e.clientX, e.clientY);
          if (pct && teamById.has(drag.athleteId)) {
            placeAthlete(drag.athleteId, pct.xPct, pct.yPct);
          }
        }
      }
    },
    [clientToFloorPercent, placeAthlete, removeAthlete, teamById],
  );

  const movePointerDrag = useCallback((e: PointerEvent) => {
    const drag = pointerDragRef.current;
    if (drag && e.pointerId === drag.pointerId) {
      const dx = e.clientX - drag.startClientX;
      const dy = e.clientY - drag.startClientY;
      const dist = Math.hypot(dx, dy);

      if (drag.awaitingLongPress) {
        drag.lastClientX = e.clientX;
        drag.lastClientY = e.clientY;
        if (dist >= ROSTER_LONG_PRESS_MOVE_CANCEL_PX) {
          if (rosterLongPressTimerRef.current !== null) {
            clearTimeout(rosterLongPressTimerRef.current);
            rosterLongPressTimerRef.current = null;
          }
          pointerDragRef.current = null;
        }
        return;
      }

      if (!drag.moved && dist >= DRAG_THRESHOLD_PX) {
        drag.moved = true;
      }
      if (drag.moved) {
        e.preventDefault();
        if (
          drag.fromPalette &&
          !paletteLockSetForThisDragRef.current
        ) {
          paletteLockSetForThisDragRef.current = true;
          setPaletteRosterDragLock(true);
        }
        setPointerGhost({
          athleteId: drag.athleteId,
          clientX: e.clientX,
          clientY: e.clientY,
        });
        const paletteEl = paletteRef.current;
        if (paletteEl) {
          const pr = paletteEl.getBoundingClientRect();
          const over =
            e.clientX >= pr.left &&
            e.clientX <= pr.right &&
            e.clientY >= pr.top &&
            e.clientY <= pr.bottom;
          setDragOverPalette(over);
        }
      }
      return;
    }
    const pan = floorPointerPanRef.current;
    if (pan && e.pointerId === pan.pointerId) {
      const dx = e.clientX - pan.lastX;
      const dy = e.clientY - pan.lastY;
      pan.lastX = e.clientX;
      pan.lastY = e.clientY;
      if (dx !== 0 || dy !== 0) pan.moved = true;
      e.preventDefault();
      setViewTransform((vt) => ({
        ...vt,
        panX: vt.panX + dx,
        panY: vt.panY + dy,
      }));
    }
  }, []);

  const handleWindowPointerUp = useCallback(
    (e: PointerEvent) => {
      endPointerDrag(e);
      const pan = floorPointerPanRef.current;
      if (pan && e.pointerId === pan.pointerId) {
        floorPointerPanRef.current = null;
        try {
          viewportRef.current?.releasePointerCapture(e.pointerId);
        } catch {
          /* ignore */
        }
        if (pan.moved) {
          suppressViewportClearRef.current = true;
        }
      }
    },
    [endPointerDrag],
  );

  useEffect(() => {
    const move = (e: PointerEvent) => movePointerDrag(e);
    window.addEventListener('pointerup', handleWindowPointerUp);
    window.addEventListener('pointercancel', handleWindowPointerUp);
    window.addEventListener('pointermove', move);
    return () => {
      window.removeEventListener('pointerup', handleWindowPointerUp);
      window.removeEventListener('pointercancel', handleWindowPointerUp);
      window.removeEventListener('pointermove', move);
    };
  }, [handleWindowPointerUp, movePointerDrag]);

  const startPointerDrag = useCallback((e: React.PointerEvent, athleteId: string) => {
    if (e.button !== 0 && e.pointerType === 'mouse') return;
    e.stopPropagation();
    suppressSelectClickRef.current = false;
    multitouchDuringFloorPointerDragRef.current = false;
    const el = e.currentTarget as HTMLElement;
    const inPalette = Boolean(paletteRef.current?.contains(el));
    const awaitingLongPress = inPalette && e.pointerType === 'touch';
    paletteLockSetForThisDragRef.current = false;

    if (rosterLongPressTimerRef.current !== null) {
      clearTimeout(rosterLongPressTimerRef.current);
      rosterLongPressTimerRef.current = null;
    }

    pointerDragRef.current = {
      athleteId,
      pointerId: e.pointerId,
      startClientX: e.clientX,
      startClientY: e.clientY,
      lastClientX: e.clientX,
      lastClientY: e.clientY,
      moved: false,
      captureEl: el,
      awaitingLongPress,
      fromPalette: inPalette,
    };

    if (awaitingLongPress) {
      const pointerIdForLongPress = e.pointerId;
      rosterLongPressTimerRef.current = window.setTimeout(() => {
        rosterLongPressTimerRef.current = null;
        const d = pointerDragRef.current;
        if (!d || d.pointerId !== pointerIdForLongPress || !d.awaitingLongPress) return;
        d.awaitingLongPress = false;
        try {
          d.captureEl?.setPointerCapture(d.pointerId);
        } catch {
          /* ignore */
        }
        d.moved = true;
        paletteLockSetForThisDragRef.current = true;
        setPaletteRosterDragLock(true);
        setPointerGhost({
          athleteId: d.athleteId,
          clientX: d.lastClientX,
          clientY: d.lastClientY,
        });
      }, ROSTER_LONG_PRESS_MS);
      return;
    }

    try {
      el.setPointerCapture(e.pointerId);
    } catch {
      /* ignore */
    }
  }, []);

  const onViewportTouchStart = useCallback(
    (e: TouchEvent) => {
      if (e.touches.length >= 2) {
        viewportSawMultitouchRef.current = true;
        multitouchDuringFloorPointerDragRef.current = true;
        cancelPointerDragForViewportPinch();
        e.preventDefault();
      }
      if (e.touches.length === 2) {
        const a = e.touches[0];
        const b = e.touches[1];
        pinchRef.current = {
          startDist: distance(a, b),
          startScale: viewTransformRef.current.scale,
        };
        panRef.current = null;
      } else if (e.touches.length === 1 && !pinchRef.current) {
        const t = e.touches[0];
        const target = e.target;
        const blocked =
          target instanceof Element &&
          Boolean(
            target.closest('.floor-layout-drag-source') ||
              target.closest('.floor-layout-team-bar') ||
              target.closest('button'),
          );
        if (!blocked) {
          panRef.current = { lastX: t.clientX, lastY: t.clientY };
        }
      }
    },
    [cancelPointerDragForViewportPinch],
  );

  const onViewportTouchMove = useCallback(
    (e: TouchEvent) => {
      const vp = viewportRef.current;
      if (!vp) return;
      const vr = vp.getBoundingClientRect();

      if (e.touches.length >= 2) {
        viewportSawMultitouchRef.current = true;
        if (pointerDragRef.current) {
          multitouchDuringFloorPointerDragRef.current = true;
          cancelPointerDragForViewportPinch();
        }
      }

      if (e.touches.length === 2 && pinchRef.current) {
        e.preventDefault();
        const a = e.touches[0];
        const b = e.touches[1];
        const d1 = distance(a, b);
        const mid = midpoint(a, b);
        const p = pinchRef.current;
        const newScale = clamp(p.startScale * (d1 / p.startDist), MIN_SCALE, MAX_SCALE);
        const midViewX = mid.x - vr.left;
        const midViewY = mid.y - vr.top;
        const vt = viewTransformRef.current;
        const innerX = (midViewX - vt.panX) / vt.scale;
        const innerY = (midViewY - vt.panY) / vt.scale;
        setViewTransform({
          scale: newScale,
          panX: midViewX - innerX * newScale,
          panY: midViewY - innerY * newScale,
        });
      } else if (e.touches.length === 1 && panRef.current && !pinchRef.current) {
        const t = e.touches[0];
        const pr = panRef.current;
        const rawDx = t.clientX - pr.lastX;
        const rawDy = t.clientY - pr.lastY;
        pr.lastX = t.clientX;
        pr.lastY = t.clientY;
        const dx = rawDx * TOUCH_PAN_DAMPING;
        const dy = rawDy * TOUCH_PAN_DAMPING;
        e.preventDefault();
        setViewTransform((prev) => ({
          ...prev,
          panX: prev.panX + dx,
          panY: prev.panY + dy,
        }));
      }
    },
    [cancelPointerDragForViewportPinch],
  );

  const onViewportTouchEnd = useCallback((e: TouchEvent) => {
    if (e.touches.length < 2) {
      pinchRef.current = null;
    }
    if (e.touches.length === 0) {
      panRef.current = null;
      armSuppressSyntheticClickAfterViewportMultitouch();
    }
  }, [armSuppressSyntheticClickAfterViewportMultitouch]);

  const onViewportTouchCancel = useCallback((e: TouchEvent) => {
    if (e.touches.length < 2) {
      pinchRef.current = null;
    }
    if (e.touches.length === 0) {
      panRef.current = null;
      armSuppressSyntheticClickAfterViewportMultitouch();
    }
  }, [armSuppressSyntheticClickAfterViewportMultitouch]);

  const onViewportWheel = useCallback((e: WheelEvent) => {
    if (!e.ctrlKey && !e.metaKey) return;
    e.preventDefault();
    const vp = viewportRef.current;
    if (!vp) return;
    const vr = vp.getBoundingClientRect();
    const focalViewX = e.clientX - vr.left;
    const focalViewY = e.clientY - vr.top;
    setViewTransform((vt) => {
      const innerX = (focalViewX - vt.panX) / vt.scale;
      const innerY = (focalViewY - vt.panY) / vt.scale;
      const factor = e.deltaY > 0 ? 0.92 : 1.08;
      const newScale = clamp(vt.scale * factor, MIN_SCALE, MAX_SCALE);
      return {
        scale: newScale,
        panX: focalViewX - innerX * newScale,
        panY: focalViewY - innerY * newScale,
      };
    });
  }, []);

  useEffect(() => {
    const el = viewportRef.current;
    if (!el || loading) return;
    const preventSelectStart = (ev: Event) => {
      ev.preventDefault();
    };
    el.addEventListener('selectstart', preventSelectStart);
    el.addEventListener('click', onFloorViewportClickCapture, true);
    el.addEventListener('touchstart', onViewportTouchStart, { passive: false });
    el.addEventListener('touchmove', onViewportTouchMove, { passive: false });
    el.addEventListener('touchend', onViewportTouchEnd);
    el.addEventListener('touchcancel', onViewportTouchCancel);
    el.addEventListener('wheel', onViewportWheel, { passive: false });
    return () => {
      el.removeEventListener('selectstart', preventSelectStart);
      el.removeEventListener('click', onFloorViewportClickCapture, true);
      el.removeEventListener('touchstart', onViewportTouchStart);
      el.removeEventListener('touchmove', onViewportTouchMove);
      el.removeEventListener('touchend', onViewportTouchEnd);
      el.removeEventListener('touchcancel', onViewportTouchCancel);
      el.removeEventListener('wheel', onViewportWheel);
    };
  }, [
    loading,
    onFloorViewportClickCapture,
    onViewportTouchStart,
    onViewportTouchMove,
    onViewportTouchEnd,
    onViewportTouchCancel,
    onViewportWheel,
  ]);

  const clearFloor = useCallback(async () => {
    if (!sessionId || !selectedTeamKey) return;
    const ref = floorLayoutDocumentRef(db, sessionId, selectedTeamKey);
    try {
      const snap = await getDoc(ref);
      if (!snap.exists()) return;
      await updateDoc(ref, { placements: {} });
    } catch (err) {
      console.error(err);
    }
  }, [sessionId, selectedTeamKey, db]);

  const onViewportPointerDown = useCallback((e: React.PointerEvent) => {
    if (e.pointerType === 'mouse' && e.button !== 0) return;
    // Touch pan uses touch listeners only; combining both doubles movement on mobile.
    if (e.pointerType === 'touch') return;
    const target = e.target;
    if (
      target instanceof Element &&
      target.closest('.floor-layout-drag-source')
    ) {
      return;
    }
    floorPointerPanRef.current = {
      pointerId: e.pointerId,
      lastX: e.clientX,
      lastY: e.clientY,
      moved: false,
    };
    try {
      viewportRef.current?.setPointerCapture(e.pointerId);
    } catch {
      /* ignore */
    }
  }, []);

  if (!sessionId) {
    return (
      <Container>
        <Card>
          <Typography variant="h2" align="center">
            Error
          </Typography>
          <div className="error-message">Session ID is missing</div>
          <Button variant="primary" fullWidth onClick={() => navigate('/join')}>
            Join a Session
          </Button>
        </Card>
      </Container>
    );
  }

  if (loading) {
    return (
      <Container className="floor-layout-loading-outer">
        <div
          className="floor-layout-loading-spinner"
          role="status"
          aria-label="Loading floor layout"
        />
      </Container>
    );
  }

  if (error) {
    return (
      <Container>
        <Card>
          <Typography variant="h2" align="center">
            Error
          </Typography>
          <div className="error-message">{error}</div>
          <Button variant="primary" fullWidth onClick={() => navigate(`/session/${sessionId}`)}>
            Back
          </Button>
        </Card>
      </Container>
    );
  }

  const { scale, panX, panY } = viewTransform;

  return (
    <Container className="floor-layout-viewport">
      <Card className="floor-layout-page">
        <div className="floor-layout-header floor-layout-hide-narrow">
          <div>
            <Typography variant="h2">Floor Layout</Typography>
            <Typography variant="body" className="floor-layout-hide-narrow">
              Drag athletes onto the floor. Drag empty floor space to pan (or one finger on touch).
              Pinch or Ctrl+scroll to zoom. Positions save to the session.
            </Typography>
          </div>
          <div className="floor-layout-header-actions">
            <Button variant="secondary" onClick={resetFloorView}>
              Fit floor
            </Button>
            <Button variant="secondary" onClick={clearFloor}>
              Clear floor
            </Button>
            <Button variant="secondary" onClick={() => navigate(`/session/${sessionId}`)}>
              Back
            </Button>
          </div>
        </div>

        <div className="floor-layout-main">
          <div
            ref={paletteRef}
            className={`floor-layout-palette${paletteRosterDragLock ? ' floor-layout-palette--roster-drag' : ''}`}
            style={{
              outline: dragOverPalette ? '2px solid #1976d2' : undefined,
              background: dragOverPalette ? '#e3f2fd' : undefined,
            }}
          >
            <div
              className="filter-controls floor-layout-palette-filters"
              aria-label="Position"
            >
              <Button
                variant={positionFilter === 'all' ? 'primary' : 'secondary'}
                onClick={() => setPositionFilter('all')}
                compact
              >
                All
              </Button>
              <Button
                variant={positionFilter === 'Top' ? 'primary' : 'secondary'}
                onClick={() => setPositionFilter('Top')}
                compact
              >
                Tops
              </Button>
              <Button
                variant={positionFilter === 'Base' ? 'primary' : 'secondary'}
                onClick={() => setPositionFilter('Base')}
                compact
              >
                Bases
              </Button>
              <Button
                variant={positionFilter === 'Third' ? 'primary' : 'secondary'}
                onClick={() => setPositionFilter('Third')}
                compact
              >
                Thirds
              </Button>
              <Button
                variant={positionFilter === 'Multi' ? 'primary' : 'secondary'}
                onClick={() => setPositionFilter('Multi')}
                compact
              >
                Multi
              </Button>
              <Button
                variant={positionFilter === 'Unknown' ? 'primary' : 'secondary'}
                onClick={() => setPositionFilter('Unknown')}
                compact
              >
                Unknown
              </Button>
            </div>
            <div
              className={
                roster.length === 0 ||
                positionFilteredRoster.length === 0 ||
                paletteAthletes.length === 0
                  ? 'floor-layout-palette-roster floor-layout-palette-roster-empty'
                  : 'floor-layout-palette-roster'
              }
            >
              {roster.length === 0 ? (
                <Typography variant="body">No athletes in this team.</Typography>
              ) : positionFilteredRoster.length === 0 ? (
                <Typography variant="body">No athletes match this position filter.</Typography>
              ) : paletteAthletes.length === 0 ? (
                <>
                  <Typography variant="body" className="floor-layout-hide-narrow">
                    Everyone in this filter is on the floor. Drag a card to the roster to remove them.
                  </Typography>
                  <Typography variant="body" className="floor-layout-show-narrow-only">
                    All on floor. Drag a token here to remove.
                  </Typography>
                </>
              ) : (
                paletteAthletes.map((a) => (
                  <div
                    key={a.id}
                    className="floor-layout-drag-source"
                    onPointerDown={(e) => startPointerDrag(e, a.id)}
                  >
                    <MiniAthleteCard
                      athlete={a}
                      selected={selectedAthleteId === a.id}
                      onSelect={() => {
                        if (consumeAthleteSelectSuppressedByPinch()) return;
                        if (suppressSelectClickRef.current) {
                          suppressSelectClickRef.current = false;
                          return;
                        }
                        setSelectedAthleteId(a.id === selectedAthleteId ? null : a.id);
                      }}
                    />
                  </div>
                ))
              )}
            </div>
          </div>

          <div className="floor-layout-canvas-wrap">
            <div
              ref={viewportRef}
              className="floor-layout-canvas-viewport"
              onPointerDown={onViewportPointerDown}
              onClick={() => {
                if (consumeAthleteSelectSuppressedByPinch()) return;
                if (suppressViewportClearRef.current) {
                  suppressViewportClearRef.current = false;
                  return;
                }
                setSelectedAthleteId(null);
              }}
            >
              <div
                className="floor-layout-canvas-transform"
                style={{
                  width: FLOOR_INNER_WIDTH,
                  height: FLOOR_INNER_HEIGHT,
                  transform: `matrix(${scale}, 0, 0, ${scale}, ${panX}, ${panY})`,
                }}
              >
                <div className="floor-layout-canvas-inner">
                  <div className="floor-layout-mat-grid" aria-hidden>
                    {MAT_DIVIDER_INDICES.map((i) => (
                      <div
                        key={i}
                        className="floor-layout-mat-line"
                        style={{ left: `${(i / MAT_COUNT) * 100}%` }}
                      />
                    ))}
                    <div className="floor-layout-center-mat-x" aria-hidden />
                    <div
                      className="floor-layout-center-mat-x floor-layout-center-mat-x--bottom"
                      aria-hidden
                    />
                  </div>
                  {Object.keys(placements).length === 0 && (
                    <div className="floor-layout-empty-hint">Drop athletes here to place them</div>
                  )}
                  {Object.entries(placements).map(([id, pos]) => {
                    const a = teamById.get(id);
                    if (!a) return null;
                    return (
                      <div
                        key={id}
                        className="floor-layout-floor-token"
                        style={{ left: `${pos.xPct}%`, top: `${pos.yPct}%` }}
                        onClick={(e) => e.stopPropagation()}
                      >
                        <div
                          className="floor-layout-drag-source floor-layout-drag-source-floor"
                          onPointerDown={(e) => startPointerDrag(e, id)}
                        >
                          <MiniAthleteCard
                            athlete={a}
                            onFloor
                            selected={selectedAthleteId === id}
                            onSelect={() => {
                              if (consumeAthleteSelectSuppressedByPinch()) return;
                              if (suppressSelectClickRef.current) {
                                suppressSelectClickRef.current = false;
                                return;
                              }
                              setSelectedAthleteId(id === selectedAthleteId ? null : id);
                            }}
                          />
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>
          </div>
        </div>
      </Card>

      {pointerGhost && teamById.get(pointerGhost.athleteId) && (
        <div
          className="floor-layout-drag-ghost"
          style={{
            left: pointerGhost.clientX,
            top: pointerGhost.clientY,
          }}
        >
          <MiniAthleteCard
            athlete={teamById.get(pointerGhost.athleteId)!}
            selected={false}
            onSelect={() => {}}
          />
        </div>
      )}

      <div className="floor-layout-team-bar">
        <span className="floor-layout-team-bar-label">Teams</span>
        {teamKeys.length === 0 ? (
          <Typography variant="body">No athletes yet</Typography>
        ) : (
          teamKeys.map((key) => (
            <Button
              key={key}
              variant={selectedTeamKey === key ? 'primary' : 'secondary'}
              compact
              onClick={() => setSelectedTeamKey(key)}
            >
              {teamLabel(key)}
            </Button>
          ))
        )}
      </div>
    </Container>
  );
}

